<template>
  <CategoryTop :title="$t('global.nav.userOrders')" :description="$t('shop.order.info')"
    :breadcrumbs-parent-routes="['account']">
    <template #right>
      <DashboardCard :border="false" class="d-flex justify-content-end">
        <template v-slot:content>
          <MDBBtn color="danger" @click="goToShop">{{
            $t("global.buttons.newOrder")
          }}</MDBBtn>
        </template>
      </DashboardCard>
    </template>
  </CategoryTop>
  <Loader :show="loading" v-if="loading"></Loader>
  <div v-else>
    <MDBRow>
      <MDBCol col="9" offsetLg="3">
        <div class="search-box p-4">
          <div class="w-100">
            <MDBRow>
              <MDBCol col="12" md="3">
                <label for="date" class="label px-3 text-uppercase">
                  {{ $t("shop.order.date") }}
                </label>
                <VueDatePicker v-model="date" :locale="currentLocale.slice(0, 2)" :enable-time-picker="false"
                  :cancelText="$t('global.buttons.cancel')" :selectText="$t('global.buttons.select')"
                  @update:model-value="search" :format="formatDatePicker" id="date"></VueDatePicker>
              </MDBCol>
              <MDBCol col="12" md="5">
                <label for="products" class="label px-3 text-uppercase">
                  {{ $t("shop.product") }}
                </label>
                <select v-if="productList.length" v-model="selectedProduct" class="form-select mb-1" @change="search"
                  id="products">
                  <option :value="null">
                    {{ $t("account.profile.choose") }}
                  </option>
                  <option :value="product.id" v-for="product in productList" :key="product.id">
                    {{ product.name }}
                  </option>
                </select>

                <select v-else v-model="selectedProduct" class="form-select mb-1" @change="search" id="products">
                  <option :value="null">
                    {{ $t("account.profile.choose") }}
                  </option>
                </select>
              </MDBCol>
              <MDBCol col="12" md="4">
                <label for="search" class="label px-3 text-uppercase">
                  {{ $t("shop.order.number") }}
                </label>
                <MDBInput v-model="query" :label="$t('global.search')" id="search" />
              </MDBCol>
            </MDBRow>
          </div>
          <span>
            <MDBBtn color="danger" @click="search">{{
              $t("global.buttons.toSearch")
            }}</MDBBtn>
          </span>
        </div>
      </MDBCol>
    </MDBRow>
    <MDBRow>
      <MDBCol col="12" lg="8">
        <div class="pt-2">
          <MDBTable borderless class="points-overview">
            <thead>
              <tr>
                <th>{{ $t("shop.order.date") }}</th>
                <th>{{ $t("shop.order.number") }}</th>
                <th>{{ $t("shop.order.products") }}</th>
              </tr>
            </thead>
            <tbody v-if="userOrders.length > 0">
              <tr v-for="(order, index) in userOrders" :key="index">
                <td>{{ dates.parseDate(order.order_date) }}</td>
                <td>{{ order.order_number }}</td>
                <td>
                  <div v-for="(item, itemIndex) in order.items" :key="itemIndex"
                    :class="{ 'order-item--removed': isRemovedItem(item) }">
                    <div class="order-item__content">
                      <div class="fw-bold">
                        {{ productName(item) }}
                        <template v-if="item.product_variant">
                          <span>({{
                            item.product_variant.title ? item.product_variant.title[currentLocale] : showAttributes(
                              item.product_variant.attribute_option
                            )
                          }})</span>
                        </template>
                      </div>
                      {{ item.quantity }} {{ $t("shop.order.quantity_short") }}
                    </div>
                    <div v-if="isRemovedItem(item)" class="order-item__removed-label">
                      {{ $t("shop.order.itemRemoved") }}
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <div>{{ $t("shop.order.anyOrder") }}</div>
            </tbody>
          </MDBTable>
        </div>
      </MDBCol>
    </MDBRow>
  </div>
</template>

<script setup>
import CategoryTop from "@/components/General/CategoryTop/index.vue";
import DashboardCard from "@/components/User/Wholesaler/DashboardCard.vue";
import { MDBRow, MDBCol, MDBBtn, MDBInput, MDBTable } from "mdb-vue-ui-kit";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { onMounted, computed, ref, watch } from "vue";
import Loader from "@/components/General/Loader/index.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import dates from "@/plugins/dates";
const store = useStore();
const router = useRouter();

const currentLocale = computed(() => store.getters.getCurrentLocale);
const userOrders = computed(() => store.getters.getUserOrders);
const loading = computed(() => store.getters.getPending);
const userProducts = computed(() => store.getters.getUserProducts);
const selectedProduct = ref(null);
const query = ref("");
const date = ref(null);

const productList = ref([]);

const fetchUserOrders = async () => {
  try {
    await store.dispatch("loadUserOrders");
  } catch (error) {
    console.log(error);
  }
};

onMounted(async () => {
  await fetchUserOrders();

  if (userProducts.value.length > 0) {
    const mappedProducts = userProducts.value.map((value) => {
      return { id: value.id, name: value.name[currentLocale.value] };
    });

    productList.value = [...productList.value, ...mappedProducts];
  }
});

const goToShop = () => {
  router.push({ name: "shop" });
};

const isRemovedItem = (item) => Boolean(item?.is_removed || item?.deleted_at);

const productName = (item) => {
  const name = item?.product?.name;
  if (name && typeof name === "object") {
    return name[currentLocale.value] || Object.values(name).find(Boolean) || "";
  }
  return name || "";
};

const showAttributes = (attribute_option) => {
  return JSON.parse(attribute_option)
    .map((item) => `${item.label}: ${item.option}`)
    .join(", ");
};

const search = async () => {
  const params = [];
  selectedProduct.value
    ? params.push(`productId=${selectedProduct.value}`)
    : "";
  query.value ? params.push(`search=${query.value}`) : "";
  date.value ? params.push(`date=${dates.parseDate(date.value)}`) : "";

  let data = "?";
  for (const element of params) {
    data += `${element}&`;
  }

  await store.dispatch("loadUserOrders", data.replace(/.$/, ""));
};

const formatDatePicker = (date) => {
  return dates.parseDate(date);
};

watch(query, async (newVal) => {
  if (newVal.length == 0) {
    await search();
  }
});
</script>

<style scoped lang="scss">
@import "@/styles/_variables.scss";

.points-overview {
  width: 100%;
  border-spacing: 3px;
  border-collapse: separate;

  td,
  th {
    padding: 5px;
    text-align: left;
  }

  th {
    background: $gray;
    color: $backgroundGray;
  }

  &--small {
    padding: 0 30px 70px 30px;
  }
}

.points-overview>tbody>tr:nth-of-type(2n + 1)>* {
  background-color: $secondaryGray;
}

:deep(.search-box) {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.order-item--removed {
  color: #c00;
  margin-bottom: 4px;

  .order-item__content {
    text-decoration: line-through;
  }
}

.order-item__removed-label {
  font-size: 0.85em;
  font-weight: 600;
}
</style>
